import { FC } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_COLORS } from 'app/modules/analytics/components/shared/charts';
import type { PerformanceChartData } from 'app/modules/analytics/modules/performance/types/chartTypes';
import NumberUtil from 'utils/number';
import styles from './AnalyticsExportChart.module.scss';

/**
 * The chart draws at a fixed logical size and the SVG is stretched to its box
 * by the stylesheet. recharts puts a `viewBox` on that SVG, so the drawing
 * scales with whatever the layout gives it — unlike a canvas, which is measured
 * once against the renderer's viewport and then printed at that size onto a
 * sheet it never saw.
 */
const LOGICAL_WIDTH = 1000;
const LOGICAL_HEIGHT = 620;

const AXIS_TICK = { fill: CHART_COLORS.axisLabel, fontSize: 22 };

interface Props {
  chartType: 'line' | 'bar';
  stacked?: boolean;
  data: PerformanceChartData;
}

const AnalyticsExportChart: FC<Props> = ({ chartType, stacked, data }) => {
  const rows = data.labels.map((label, index) => {
    const row: Record<string, string | number> = { label };

    data.datasets.forEach((dataset) => {
      row[dataset.label] = dataset.data[index] ?? 0;
    });

    return row;
  });

  const axes = (
    <>
      <CartesianGrid
        stroke={CHART_COLORS.gridLine}
        strokeWidth={2}
        vertical={false}
      />
      <XAxis
        dataKey="label"
        tick={AXIS_TICK}
        tickLine={false}
        axisLine={false}
        interval="preserveStartEnd"
        minTickGap={24}
        tickMargin={16}
      />
      <YAxis
        tick={AXIS_TICK}
        tickLine={false}
        axisLine={false}
        width={88}
        tickFormatter={(value: number) => NumberUtil.compact(value)}
      />
    </>
  );

  const chartProps = {
    width: LOGICAL_WIDTH,
    height: LOGICAL_HEIGHT,
    data: rows,
    margin: { top: 16, right: 16, bottom: 16, left: 0 },
  };

  return (
    <div className={styles.chart}>
      {chartType === 'bar' ? (
        <BarChart {...chartProps}>
          {axes}
          {data.datasets.map((dataset) => (
            <Bar
              key={dataset.label}
              dataKey={dataset.label}
              fill={dataset.color}
              stackId={stacked ? 'stack' : undefined}
              isAnimationActive={false}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      ) : (
        <LineChart {...chartProps}>
          {axes}
          {data.datasets.map((dataset) => (
            <Line
              key={dataset.label}
              type="monotone"
              dataKey={dataset.label}
              stroke={dataset.color}
              strokeWidth={3}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      )}
    </div>
  );
};

export default AnalyticsExportChart;
